// A3 (Foydalanuvchilar) — qo'shimcha ekranlar uchun module-local data-qatlam.
// `lib/adapters.ts` / `i18n/*` HOT fayllariga TEGILMAYDI (import qilinadi, tahrir
// EMAS). Ikki toifa endpoint:
//   · JONLI (real)  — GET /admin/users, GET /admin/audit/pii, POST /admin/points/grant,
//     POST /admin/users/:id/strike|warn|force-logout (main.go da registratsiya qilingan).
//   · GREENFIELD    — username/scrub/delete/restore/shadowban/impersonate/profile-history/
//     segmentlar/import: backend patch chiqqach ishlaydi. Fetcher fail-soft (bo'sh),
//     mutatsiya POST qiladi va 404 bo'lsa chaqiruvchi «kutilmoqda» deб ko'rsatadi
//     (developers/devApi.ts + UsersScreen.forceLogout naqshi).
import { api, ApiError } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

/** 404 (endpoint hali deploy bo'lmagan) — «buzuq tugma» emas, «kutilmoqda». */
export function isPending(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404
}

/** Klient-tomon sahifalash — DataTable page 1-asosli (adapters.ts bilan bir xil). */
function paginate<T>(rows: T[], params: FetchParams): FetchResult<T> {
  const start = (params.page - 1) * params.pageSize
  return { rows: rows.slice(start, start + params.pageSize), total: rows.length }
}

/* ── A3.23 — PII kirish jurnali (JONLI: GET /admin/audit/pii, superadmin-only) ── */

export interface PiiAccessRow {
  id: number
  admin_id?: number | null
  admin?: string | null
  action: string
  category: string
  target?: string | null
  detail?: string | null
  ip?: string | null
  created_at: number
}
export async function fetchPiiAccessLog(params: FetchParams): Promise<FetchResult<PiiAccessRow>> {
  try {
    const data = await api.get<{ items: PiiAccessRow[] }>('/admin/audit/pii', {
      query: { limit: 300 },
      signal: params.signal,
    })
    const q = params.search.trim().toLowerCase()
    const rows = (data.items ?? []).filter(
      (r) => !q || `${r.admin ?? ''} ${r.category} ${r.target ?? ''}`.toLowerCase().includes(q),
    )
    return paginate(rows, params)
  } catch (e) {
    // superadmin bo'lmasa 403 — bu HALOL cheklov, PermissionDenied ekran ko'rsatadi.
    // Endpoint yo'q bo'lsa (404) — bo'sh holat.
    if (e instanceof ApiError && (e.status === 404 || e.status === 403)) return paginate([], params)
    throw e
  }
}
export async function fetchPiiCategories(signal?: AbortSignal): Promise<string[]> {
  try {
    const data = await api.get<{ items: string[] }>('/admin/audit/pii/categories', signal ? { signal } : {})
    return data.items ?? []
  } catch {
    return []
  }
}

/* ── A3.36 — Premium/Stars berish (JONLI: POST /admin/points/grant) ─────────────
 * MyGram'da Stars ekvivalenti = ball iqtisodi. Musbat = berish, manfiy = yechish.
 * finance.economy.grant huquqi bilan gated (serverda majburlanadi). */
export function grantUserReward(userId: number, amount: number, reason: string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/admin/points/grant', {
    user_id: userId,
    amount,
    reason,
  })
}

/* ── A3.32 — Username majburan o'zgartirish (greenfield POST) ──────────────────
 * @username'ni majburan bo'shatish/almashtirish (spam/impersonation). Guruh
 * uchun analog jonli: POST /admin/groups/:id/release-username. */
export function forceUsername(userId: number, username: string, reason: string): Promise<unknown> {
  return api.post(`/admin/users/${userId}/username`, { username: username.trim(), reason })
}

/* ── A3.33 — Avatar/nom o'chirish (greenfield POST) ────────────────────────────
 * Nomaqbul avatar yoki ko'rsatiladigan nomni tozalash (moderatsiya). */
export type ScrubField = 'avatar' | 'name'
export function scrubUserProfile(userId: number, fields: ScrubField[], reason: string): Promise<unknown> {
  return api.post(`/admin/users/${userId}/scrub`, { fields, reason })
}

/* ── A3.34/A3.35 — Akkauntni o'chirish / tiklash (greenfield POST) ─────────────
 * Ma'muriy o'chirish (soft-delete, tiklanadigan) va tiklash. GDPR o'chirish
 * self-service jonli (POST /me/delete); bu ma'muriy tomoni. */
export function deleteAccount(userId: number, reason: string): Promise<unknown> {
  return api.post(`/admin/users/${userId}/delete`, { reason })
}
export function restoreAccount(userId: number, reason: string): Promise<unknown> {
  return api.post(`/admin/users/${userId}/restore`, { reason })
}

/* ── A3.30 — Shadowban (greenfield POST) ───────────────────────────────────────
 * Spam uchun: foydalanuvchi post qiladi deб o'ylaydi, lekin boshqalarga
 * ko'rinmaydi. Foydalanuvchiga bildirilmaydi (jim). */
export function setShadowban(userId: number, enabled: boolean, reason: string): Promise<unknown> {
  return api.post(`/admin/users/${userId}/shadowban`, { enabled, reason })
}
export async function fetchShadowbanState(userId: number, signal?: AbortSignal): Promise<boolean> {
  try {
    const d = await api.get<{ shadowbanned: boolean }>(`/admin/users/${userId}/shadowban`, signal ? { signal } : {})
    return Boolean(d.shadowbanned)
  } catch {
    return false
  }
}

/* ── A3.08 — Profil tarixi (greenfield GET, fail-soft) ─────────────────────────
 * Nom/username/avatar o'zgarishlar jurnali (kim → nima → qachon). */
export interface ProfileChange {
  id: number
  field: string // username | first_name | last_name | avatar | bio
  old_value?: string | null
  new_value?: string | null
  changed_by?: string | null // 'user' yoki admin username
  created_at: number
}
export async function fetchProfileHistory(userId: number, signal?: AbortSignal): Promise<ProfileChange[]> {
  try {
    const d = await api.get<{ items: ProfileChange[] }>(
      `/admin/users/${userId}/profile-history`,
      signal ? { signal } : {},
    )
    return d.items ?? []
  } catch {
    return []
  }
}

/* ── A3.37 — Impersonation (greenfield stub, FAQAT test muhiti) ────────────────
 * Debug uchun foydalanuvchi sifatida kirish. Prod'da SERVER RAD etadi (bu
 * xavfsizlik chegarasi serverда). Klient faqat so'rov yuboradi. */
export interface ImpersonateResult {
  token?: string | null
  expires_at?: number | null
}
export function impersonateUser(userId: number, reason: string): Promise<ImpersonateResult> {
  return api.post<ImpersonateResult>(`/admin/users/${userId}/impersonate`, { reason })
}

/* ── A3.25 — Xabarlarni ko'rish (L4: huquqiy so'rov + four-eyes, greenfield) ────
 * Eng maxfiy PII darajasi. Xabar KONTENTI bu yerдa KO'RSATILMAYDI — faqat
 * huquqiy asos bilan kirish SO'ROVI yaratiladi (four-eyes tasdig'iga boradi).
 * Grant qilingan kirish jurnali A3.23 (PII log)да paydo bo'ladi. */
export interface MessageAccessGrant {
  status?: string // 'pending' | 'granted'
  request_id?: number | null
}
export function requestMessageAccess(
  userId: number,
  legalRef: string,
  reason: string,
): Promise<MessageAccessGrant> {
  return api.post<MessageAccessGrant>(`/admin/users/${userId}/message-access`, {
    legal_ref: legalRef.trim(),
    reason,
  })
}

/* ── A3.03 — Saqlangan segmentlar (localStorage + optional endpoint, fail-soft) ─
 * Filtr to'plamini nomlab saqlash → keyin bir bosishда qo'llash. Server segment
 * endpointи chiqmaguncha mahalliy (brauzerда) saqlanadi. */
const SEG_KEY = 'grom.admin.user-segments'
export interface UserSegment {
  id: string
  name: string
  query: string // tez-qidiruv matni yoki filtr tavsifi
  created_at: number
}
export function loadSegments(): UserSegment[] {
  try {
    const raw = localStorage.getItem(SEG_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw) as UserSegment[]
    return Array.isArray(arr) ? arr : []
  } catch {
    return []
  }
}
export function saveSegment(name: string, query: string): UserSegment[] {
  const list = loadSegments()
  const seg: UserSegment = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: name.trim(),
    query: query.trim(),
    created_at: Date.now(),
  }
  const next = [seg, ...list].slice(0, 100)
  try {
    localStorage.setItem(SEG_KEY, JSON.stringify(next))
  } catch {
    /* kvota to'lgan — jim */
  }
  return next
}
export function deleteSegment(id: string): UserSegment[] {
  const next = loadSegments().filter((s) => s.id !== id)
  try {
    localStorage.setItem(SEG_KEY, JSON.stringify(next))
  } catch {
    /* jim */
  }
  return next
}

/* ── A3.06 — Import (FAQAT test muhiti, greenfield stub) ────────────────────────
 * CSV/JSON dan test foydalanuvchilar yaratish. Prod'da SERVER RAD etadi. */
export interface ImportOutcome {
  created?: number
  skipped?: number
  errors?: string[]
}
export function importUsers(payload: string, dryRun: boolean): Promise<ImportOutcome> {
  return api.post<ImportOutcome>('/admin/users/import', { payload, dry_run: dryRun })
}

/** Module-local o'zbekcha satrlar (i18n HOT → keyin keyга ko'chiriladi). */
export const USERS_STR = {
  back: 'Karta',
  toList: 'Foydalanuvchilar',
  reason: 'Sabab',
  reasonPh: 'Sababni yozing (auditga tushadi)…',
  apply: 'Qo‘llash',
  done: 'Bajarildi',
  failed: 'Bajarilmadi',
  pending: 'Endpoint hali tayyor emas (backend patch kutilmoqda)',
  empty: 'Ma’lumot yo‘q',

  // A3.02 kengaytirilgan qidiruv
  searchTitle: 'Kengaytirilgan qidiruv',
  searchHint: 'Ustun filtrlari (ⓕ) va tez-qidiruv orqali 20+ mezon bo‘yicha filtrlang.',

  // A3.03 segmentlar
  segTitle: 'Saqlangan segmentlar',
  segName: 'Segment nomi',
  segQuery: 'Qidiruv / filtr',
  segSave: 'Saqlash',
  segEmpty: 'Saqlangan segment yo‘q',
  segLocalNote: 'Segmentlar hozircha shu brauzerда saqlanadi (server segment API kutilmoqda).',
  segOpen: 'Ochish',
  segDelete: 'O‘chirish',

  // A3.04 ommaviy amallar
  bulkTitle: 'Ommaviy amallar',
  bulkHint: 'Qatorlarni belgilang, so‘ng amalni tanlang. Har bir amal auditga tushadi.',
  bulkWarn: 'Ommaviy ogohlantirish',
  bulkStrike: 'Ommaviy strayk (1-daraja)',
  bulkOnlyRows: 'Iltimos aniq qatorlarni belgilang (butun filtr bo‘yicha ommaviy amal bloklangan).',
  bulkRunning: (n: number) => `Bajarilyapti… (${n})`,
  bulkResult: (ok: number, fail: number) => `Tayyor: ${ok} ta muvaffaqiyatli, ${fail} ta xato`,
  message: 'Xabar',
  messagePh: 'Ogohlantirish matni…',

  // A3.05 eksport
  exportTitle: 'Eksport (CSV)',
  exportHint: 'Joriy foydalanuvchi ro‘yxatini CSV faylga yuklab oling (four-eyes: yirik eksport auditlanadi).',
  exportBtn: 'CSV yuklab olish',
  exportLimit: 'Yozuvlar soni',
  exporting: 'Tayyorlanmoqda…',

  // A3.06 import
  importTitle: 'Import (test muhiti)',
  importHint: 'CSV yoki JSON. FAQAT test muhiti — prod serverда rad etiladi.',
  importDryRun: 'Sinov (dry-run) — hech narsa yozilmaydi',
  importBtn: 'Import qilish',
  importDone: (o: ImportOutcome) => `Yaratildi: ${o.created ?? 0}, o‘tkazib yuborildi: ${o.skipped ?? 0}`,

  // A3.08 profil tarixi
  histTitle: 'Profil tarixi',
  histHint: 'Nom, username va avatar o‘zgarishlari.',
  histField: 'Maydon',
  histOld: 'Eski',
  histNew: 'Yangi',
  histBy: 'Kim',

  // A3.23 PII jurnali
  piiTitle: 'PII kirish jurnali',
  piiHint: 'Shaxsiy ma’lumotни kim, qachon, nima uchun ko‘rgani (superadmin-only).',
  piiCat: 'Toifa',
  piiSubject: 'Obyekt',
  piiAdmin: 'Xodim',

  // A3.24 GDPR eksport
  gdprTitle: 'Ma’lumot eksporti (GDPR)',
  gdprHint: 'Foydalanuvchining barcha ma’lumotini bitta JSON faylga yig‘adi (karta, hamyon, kirish tarixi, strayk, ogohlantirish, izohlar).',
  gdprBtn: 'JSON yuklab olish',
  gdprBuilding: 'Yig‘ilmoqda…',

  // A3.25 xabarlarni ko'rish (L4)
  msgTitle: 'Xabarlarni ko‘rish (L4)',
  msgHint: 'Eng maxfiy daraja. Xabar kontenti bu yerда KO‘RSATILMAYDI — huquqiy asos bilan kirish so‘rovi yaratiladi (four-eyes tasdig‘iga boradi).',
  msgLegalRef: 'Huquqiy asos (ish/so‘rov raqami)',
  msgLegalPh: 'Masalan: LEA-2026-014',
  msgRequest: 'Kirish so‘rovi yaratish',
  msgRequested: 'So‘rov yuborildi (four-eyes tasdig‘i kutilmoqda)',

  // A3.27 cheklov (strayk)
  restrictTitle: 'Cheklov qo‘yish (strayk)',
  restrictHint: 'Strayk = progressiv jazo. Aktiv ballar yig‘ilib borad.',
  severity: 'Og‘irlik',
  issue: 'Strayk berish',
  issued: 'Strayk berildi',
  activePoints: 'Aktiv ballar',

  // A3.30 shadowban
  shadowTitle: 'Shadowban',
  shadowHint: 'Spam uchun jim cheklov: foydalanuvchi post qiladi deб o‘ylaydi, lekin boshqalarга ko‘rinmaydi. Unga bildirilmaydi.',
  shadowOn: 'Shadowban yoqilgan',
  shadowOff: 'Shadowban o‘chirilgan',
  shadowEnable: 'Yoqish',
  shadowDisable: 'O‘chirish',

  // A3.32 username
  unameTitle: 'Username majburan o‘zgartirish',
  unameHint: 'Nomaqbul/impersonatsiya @username‘ni bo‘shatish yoki almashtirish.',
  unameNew: 'Yangi username (bo‘sh = faqat bo‘shatish)',
  unameApply: 'Qo‘llash',

  // A3.33 avatar/nom o'chirish
  scrubTitle: 'Avatar / nom o‘chirish',
  scrubHint: 'Nomaqbul avatar yoki ko‘rsatiladigan nomni tozalash.',
  scrubAvatar: 'Avatarni o‘chirish',
  scrubName: 'Nomni o‘chirish',
  scrubApply: 'Tozalash',
  scrubPick: 'Kamida bittasini tanlang',

  // A3.34 o'chirish
  delTitle: 'Akkauntni o‘chirish (ma’muriy)',
  delHint: 'Soft-delete — tiklanadi. Foydalanuvchi kira olmaydi.',
  delBtn: 'Akkauntni o‘chirish',

  // A3.35 tiklash
  restoreTitle: 'Akkauntni tiklash',
  restoreHint: 'Oldin o‘chirilgan akkauntni qaytarish.',
  restoreBtn: 'Tiklash',

  // A3.36 premium/stars
  rewardTitle: 'Premium / Stars berish',
  rewardHint: 'Kompensatsiya sifatida ball (Stars ekvivalenti) berish yoki yechish. Musbat = berish, manfiy = yechish.',
  rewardAmount: 'Miqdor (± Stars)',
  rewardGrant: 'Berish',
  rewardGranted: 'Berildi',

  // A3.37 impersonation
  impTitle: 'Impersonation (test)',
  impHint: 'Debug uchun foydalanuvchi sifatida kirish. FAQAT test muhiti — prod serverда rad etiladi.',
  impBtn: 'Impersonatsiya token olish',
  impToken: 'Vaqtinchalik token (test)',
} as const
